import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@quintoandar-tokko/jopijs';
import PendingCard from '@/components/Dashboard/DashboardPendings/PendingsCard';
import DashboardEmptyStateCard from '@/components/Dashboard/DashboardEmptyStateCard';
import RadioButtonsGroup from '@/components/Dashboard/RadioButtonsGroup';
import useFetchDashboard from '@/components/Dashboard/customHooks/useFetchDashboard';
import { showModalTask } from '@/store/slices/components/tasks/TasksSlice';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import TasksIcon from '@/public/images/dashboard/tasks.svg';
import styles from './overviewTasks.module.scss';
import { setOverviewTasks } from '@/store/slices/components/dashboard/DashboardSlice';
import { getOverviewTasks } from '@/store/slices/components/dashboard/service';
import { TASKS_OPTIONS } from '@/components/Dashboard/constants';
import OverviewTasksRow from './OverviewTasksRow';

export default function OverviewTasks() {
  const dispatch = useDispatch();
  const {
    counts,
    tasks,
    selectedOption,
    loading,
    hasFetched,
    error,
    overviewTasksRefreshCount,
  } = useSelector((state) => state.DashboardSlice.overviewTasks);
  const { refreshCount: tasksRefreshCount } = useSelector(
    (state) => state.TasksSlice
  );
  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();
  const allowedOptions = [
    {
      id: TASKS_OPTIONS.TODAY,
      text: Trans(translations, 'Today'),
    },
    {
      id: TASKS_OPTIONS.EXPIRED,
      text: Trans(translations, 'Expired'),
    },
  ];
  const targetTasks = tasks[selectedOption] ?? [];

  const handleClickRadio = (option) => {
    dispatch(setOverviewTasks({ key: 'selectedOption', value: option.id }));
  };

  useFetchOnViewport(
    (query) => {
      dispatch(getOverviewTasks(query));
    },
    [tasksRefreshCount, overviewTasksRefreshCount],
    { hasFetched }
  );

  useEffect(() => {
    if (
      counts[TASKS_OPTIONS.TODAY] === 0 &&
      counts[TASKS_OPTIONS.EXPIRED] > 0
    ) {
      dispatch(
        setOverviewTasks({
          key: 'selectedOption',
          value: TASKS_OPTIONS.EXPIRED,
        })
      );
      return;
    }

    if (
      counts[TASKS_OPTIONS.EXPIRED] === 0 &&
      counts[TASKS_OPTIONS.TODAY] > 0
    ) {
      dispatch(
        setOverviewTasks({ key: 'selectedOption', value: TASKS_OPTIONS.TODAY })
      );
    }
  }, [counts, dispatch]);

  const empty =
    counts[TASKS_OPTIONS.TODAY] === 0 && counts[TASKS_OPTIONS.EXPIRED] === 0;

  const title =
    selectedOption === TASKS_OPTIONS.TODAY
      ? Trans(
          translations,
          'CountPendingTasksForToday',
          String(counts[TASKS_OPTIONS.TODAY])
        )
      : Trans(
          translations,
          'CountOverdueTasks',
          String(counts[TASKS_OPTIONS.EXPIRED])
        );

  return (
    <PendingCard ref={ref} loading={loading || isStale}>
      <PendingCard.Header
        redirectUrl="/tasks"
        toolTipText={Trans(translations, 'ShowAll')}
      >
        {title}
      </PendingCard.Header>

      {empty && !error ? (
        <DashboardEmptyStateCard
          icon={TasksIcon}
          title={Trans(translations, 'HereYouWillSeeYourPendingTasks')}
          buttonText={Trans(translations, 'NewTask')}
          onButtonClick={() => dispatch(showModalTask(true))}
        />
      ) : (
        <Box className={styles.content}>
          <RadioButtonsGroup
            allowedOptions={allowedOptions}
            selectedOption={selectedOption}
            onClickRadio={handleClickRadio}
          />

          <Box className={styles.list}>
            {targetTasks.length > 0 ? (
              targetTasks.map((task) => (
                <OverviewTasksRow
                  key={task.id}
                  task={task}
                  isOverdue={selectedOption === TASKS_OPTIONS.EXPIRED}
                />
              ))
            ) : (
              <Text className={styles.emptyListText}>
                {Trans(translations, 'NoTasksFound')}
              </Text>
            )}
          </Box>
        </Box>
      )}
    </PendingCard>
  );
}
